import { trackMealCalendarClick, trackMealItemClick } from 'analytics/trackers/nutrition';
import { useCanAccessMealPlan } from 'navigation/useCanAccessMealPlan';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GET_MEAL_PLAN } from 'store/nutrition/actionTypes';
import { selectIsLoading, selectMealPlan } from 'store/nutrition/selectors';

import { countDayIndex, isBeforeToday, toISODateString } from 'helpers/dateParser';

import Skeleton from 'components/Skeleton';

import { MealList } from '../components/MealList/MealList';

type MealPlanState = 'loading' | 'empty' | 'filled';

const getMealState = (isLoading: boolean, hasPlan: boolean): MealPlanState => {
    if (isLoading) {
        return 'loading';
    }
    if (hasPlan) {
        return 'filled';
    }

    return 'empty';
};

export const useGetMealPlan = () => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const dispatch = useDispatch();
    const plan = useSelector(selectMealPlan);
    const isLoading = useSelector(selectIsLoading);
    const hasMealsAvailable = useCanAccessMealPlan();

    const copy = {
        minutes: 'min · ',
        calories: 'kcal',
    };

    const getMealPlan = React.useCallback(
        (date: Date) => {
            if (isBeforeToday(date)) {
                return;
            }

            dispatch({ type: GET_MEAL_PLAN, payload: { date: toISODateString(date) } });
        },
        [dispatch]
    );

    const state = isBeforeToday(selectedDate) ? 'empty' : getMealState(isLoading, !!plan);

    const handleSetSelectedDate = (date: Date) => {
        setSelectedDate(date);
        trackMealCalendarClick({ targetDay: countDayIndex(date), hasMealsAvailable });
    };

    const handleMealClick = (mealType: string) => {
        trackMealItemClick({ mealType });
    };

    const contentMap: Record<MealPlanState, React.ReactElement | null> = {
        loading: <Skeleton height={472} />,
        empty: null,
        filled: <MealList plan={plan} copy={copy} onMealClick={handleMealClick} selectedDate={selectedDate} />,
    };

    return { listContent: contentMap[state], getMealPlan, selectedDate, setSelectedDate: handleSetSelectedDate, state };
};
